import { cn } from "@/lib/utils";
import { SquareDashed } from "lucide-react";

interface EmptyStateProps {
  icon?: React.ReactNode;
  text?: string;
  className?: string;
  children?: React.ReactNode;
}

/**
 * @example
 * import { Inbox } from "lucide-react"
 * import { Button } from "@/components/ui/button"
 * import EmptyState from "@/components/ui/empty-state"
 *
 * export function NoCreators({ onSearch }: { onSearch: () => void }) {
 *   return (
 *     <EmptyState icon={<Inbox />} text="No creators in this campaign yet">
 *       <Button size="sm" onClick={onSearch}>Find creators</Button>
 *     </EmptyState>
 *   )
 * }
 */
const EmptyState = ({ 
  icon = <SquareDashed />, 
  text = "", 
  className, 
  children 
}: EmptyStateProps) => {
  return (
    <div
      className={cn(
        "relative mx-auto size-48 duration-200 [--color-border:color-mix(in_oklab,var(--color-zinc-950)10%,transparent)] group-hover:[--color-border:color-mix(in_oklab,var(--color-zinc-950)20%,transparent)] dark:[--color-border:color-mix(in_oklab,var(--color-white)15%,transparent)] dark:group-hover:bg-white/5 dark:group-hover:[--color-border:color-mix(in_oklab,var(--color-white)20%,transparent)]",
        className
      )}
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-size-[24px_24px]"
      />
      <div
        aria-hidden
        className="bg-radial to-background absolute inset-0 from-transparent to-75%"
      />
      <div className="bg-background absolute inset-0 m-auto flex size-12 items-center justify-center border-l border-t [&_svg]:size-5 [&_svg]:shrink-0 [&_svg]:opacity-80">
        {icon}
      </div>
      <div className="w-full absolute -bottom-2 m-auto flex flex-col items-center justify-center gap-1">
        <p className="text-center w-full text-xs text-muted-foreground">
          {text}
        </p>
        <div className="w-auto">{children}</div>
      </div>
    </div>
  );
};

export default EmptyState;
